import { useCallback, useEffect, useRef } from 'react';

const useEventListener = (
  eventName: string,
  handler: (e: Event) => void,
  element = window,
  // Pass `true` (or { capture: true }) to listen in the capture phase — fires
  // top-down before the target, so a global shortcut can preventDefault even if
  // the focused element would otherwise stop the event's propagation.
  options?: boolean | AddEventListenerOptions
) => {
  const savedHandler = useRef<EventListener>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  const handleEventListener = useCallback((event: Event) => {
    savedHandler.current?.(event);
  }, []);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On
      const isSupported = element && element.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref

      // Add event listener
      element.addEventListener(eventName, handleEventListener, options);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, handleEventListener, options);
      };
    },
    [eventName, element, options] // Re-run if eventName / element / options change
  );
};

export default useEventListener;
